import { ComponentProps, useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation } from "react-router";
import { ResponseData } from "@api/internal/utils/types";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import Card from "@particles/card/Card";
import { getMyAccountReadingHistoryCardsProps } from "@routes/MyAccount/utils/helpers";
import {
  FeedReadingHistoryData,
  useGetRecentArticleListProps,
} from "@routes/MyAccount/utils/types";
import axios from "axios";

/**
 * Hook to get recent article list for reading history.
 * Returns the list of recently read articles, a ref for infinite scrolling,
 * a function to handle deletion of reading history, and a list of deleted article IDs.
 */
export const useGetReadingHistory = ({
  pageSize,
}: useGetRecentArticleListProps) => {
  const { pathname } = useLocation();
  const { visitorId } = useUserInfo();

  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [readArticleList, setReadArticleList] = useState<
    ComponentProps<typeof Card>[]
  >([]);
  const [deleteArticleIdList, setDeleteArticleIdList] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchData = useCallback(
    async (readArticleList: ComponentProps<typeof Card>[]) => {
      if (!visitorId) return;

      setIsLoading(true);
      const page = readArticleList.length / pageSize + 1;
      const payload = {
        size: pageSize,
        page: page,
        urlPath: pathname,
        platform: "web",
      };

      const response = await axios
        .get<ResponseData<FeedReadingHistoryData[]>>(
          "/_plat/api/v1/user/reading-history",
          {
            params: payload,
            headers: {
              aovisitorid: visitorId,
            },
          }
        )
        .then((data) => data.data);

      if (!response.success) {
        setHasMoreStories(false);
        setIsLoading(false);
        // eslint-disable-next-line no-console
        console.error(
          `Error get reading history - ${JSON.stringify({ payload, response })}`
        );

        return;
      }

      if (response.data.length < pageSize) {
        setHasMoreStories(false);
      }

      const newData = getMyAccountReadingHistoryCardsProps(
        response.data,
        "recent_read"
      );

      setReadArticleList([...newData, ...readArticleList]);
      setIsLoading(false);
    },
    [pageSize, visitorId, pathname]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (readArticleList.length > 0) return;
    if (!visitorId) return;

    fetchData(readArticleList);
  }, [fetchData, readArticleList, visitorId]);

  const { ref } = useInView({
    threshold: 0,
    onChange: (inView) => {
      if (!inView) return;
      if (!hasMoreStories) return;
      if (readArticleList.length < pageSize) return;

      fetchData(readArticleList);
    },
  });

  const deleteArticleId = useCallback(
    async (articleId: string) => {
      const payload = {
        articleId: articleId,
      };

      const response = await axios
        .delete("/_plat/api/v1/user/reading-history", {
          data: payload,
          headers: {
            aovisitorid: visitorId,
          },
        })
        .then((data) => data.data);

      if (!response.success) {
        // eslint-disable-next-line no-console
        console.error(
          `Error delete reading history - ${JSON.stringify({ payload, response })}`
        );
      }
    },
    [visitorId]
  );

  const handleDeleteReadingHistory = (articleId: string) => {
    const newDeleteList = [...deleteArticleIdList, articleId];
    setDeleteArticleIdList(newDeleteList);

    deleteArticleId(articleId);
  };

  return {
    ref,
    readArticleList,
    handleDeleteReadingHistory,
    deleteArticleIdList,
    isLoading,
  };
};
